import { html, nothing } from "lit";
import { MULTI_AGENT_HINTS } from "../control-hints.ts";

/**
 * Verbs that turn "agents" into a *request* to delegate.
 *
 * A mention is not a request. "the multi-agent system is broken" is a bug
 * report; "Multi-agent tasks on the main session only get HEARTBEAT_OK" is
 * the error message itself. Both were detected as delegation requests, and
 * the send guard then refused the message — observed 2026-10-20, when a
 * pasted write-up that merely *mentioned* multi-agent could not be sent,
 * and the operator could not even paste the error back to report it.
 *
 * The past-tense forms ("we built a multi-agent system") are deliberately
 * absent: a narration about agent systems is not a delegation request.
 */
const DELEGATION_VERBS = [
  "use",
  "using",
  "spawn",
  "launch",
  "delegate",
  "assign",
  "orchestrate",
  "parallelize",
  "parallelise",
  "fan out",
  "fan it out",
  "build",
  "create",
  "make",
  "design",
  "implement",
  "set up",
];

/** The things that can be delegated to. */
const AGENT_NOUNS = [
  "multi-agent",
  "multi agent",
  "multiple agents",
  "multiple agent",
  "team of agents",
  "several agents",
  "agents in parallel",
  "agent team",
];

/**
 * Request shapes.
 *
 * 1. A delegation verb followed by the agent words ("use multiple agents",
 *    "build a multi-agent system").
 * 2. The agent words followed by a directive ("multiple agents, please",
 *    "a team of agents should handle this").
 * 3. An explicit fan-out ("fan this out across the agents").
 */
const MULTI_AGENT_PATTERNS: RegExp[] = [
  new RegExp(
    `\\b(?:${DELEGATION_VERBS.join("|")})\\b[^.!?\\n]{0,40}\\b(?:${AGENT_NOUNS.join("|")})\\b`,
    "i",
  ),
  new RegExp(
    `\\b(?:${AGENT_NOUNS.join("|")})\\b[^.!?\\n]{0,30}\\b(?:please|should|must|need(?:s)? to|have to|would like to|can you)\\b`,
    "i",
  ),
  /\bfan\s?(?:it|this|them|the\s+\w+)?\s*out\b[^.!?\n]{0,40}\b(?:agents?|workers?|lanes?)\b/i,
];

/**
 * Does this message *ask* for a team of agents?
 *
 * Used for two things: the advisory hint above the composer, and the
 * decision of whether a send needs a delegation target.
 *
 * It is deliberately conservative. The cost of a false positive is a refused
 * message the operator cannot send; the cost of a false negative is a
 * message that stays on one agent, which the operator can still delegate by
 * typing `@all`.
 */
export function detectMultiAgentIntent(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < 12) {
    return false;
  }
  if (/^@\S+/s.test(trimmed) || /^\/best-of-n\b/i.test(trimmed)) {
    return false;
  }
  return MULTI_AGENT_PATTERNS.some((pattern) => pattern.test(trimmed));
}

export function buildAllDelegationMessage(text: string): string {
  const trimmed = text.trim();
  const stripped = trimmed.replace(/^(please\s+)?(build|create|make|design)\s+(a\s+)?/i, "").trim();
  const task = stripped || trimmed;
  return `@all ${task}`;
}

export type MultiAgentHintProps = {
  draft: string;
  dismissed: boolean;
  onApplyAll: (nextDraft: string) => void;
  onDismiss: () => void;
};

export function renderMultiAgentHint(props: MultiAgentHintProps) {
  if (props.dismissed || !detectMultiAgentIntent(props.draft)) {
    return nothing;
  }

  return html`
    <div class="chat-multi-agent-hint callout info" role="status" aria-live="polite">
      <p class="chat-multi-agent-hint__text">
        Sounds like a team job. Your message will stay on this session unless you delegate — try
        <code>@all</code>, <code>@agentId</code>, or turn on
        <strong>Orchestrate with @all</strong> below.
      </p>
      <div class="chat-multi-agent-hint__actions">
        <button
          type="button"
          class="btn primary"
          title=${MULTI_AGENT_HINTS.useAll}
          @click=${() => props.onApplyAll(buildAllDelegationMessage(props.draft))}
        >
          Use @all
        </button>
        <button
          type="button"
          class="btn"
          title=${MULTI_AGENT_HINTS.showSyntax}
          @click=${() => {
            const el = document.querySelector(".chat-compose__hints");
            el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }}
        >
          / and @
        </button>
        <button
          type="button"
          class="btn"
          title=${MULTI_AGENT_HINTS.dismiss}
          @click=${props.onDismiss}
        >
          ${MULTI_AGENT_HINTS.dismiss}
        </button>
      </div>
    </div>
  `;
}
